import React, { useState, useEffect, useMemo } from "react";
import ReportLayout, {
  DateRangePicker,
  StatusBadge,
  ProgressBar,
} from "./components/ReportLayout";
import {
  LayoutGrid,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  ArrowUpRight,
  Timer,
  Armchair,
  Building2,
  ChevronDown,
} from "lucide-react";
import useTableUtilization from "@/hooks/useTableUtilization";
import { sectionService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

export default function TableUtilizationReport() {
  const { hasPermission } = useAuth();

if (!hasPermission(P.REPORTS.OPERATIONS.SCREEN)) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>
        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}
  const formatDate = (d) =>
    d instanceof Date ? d.toISOString().split("T")[0] : d;

  const today = formatDate(new Date());

  const [isCustomDate, setIsCustomDate] = useState(false);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [selectedSection, setSelectedSection] = useState("");

  const [sortColumn, setSortColumn] = useState("utilizationScore");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, summary, branchSummary, totalItems, loading, error, setFilters, refresh } =
    useTableUtilization();

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  const applyPreset = (preset, page = 1, size = pageSize) => {
    setCurrentPage(page);
    setIsCustomDate(false);

    setFilters((prev) => ({
      ...prev,
      period: preset,
      startDate: null,
      endDate: null,
      sectionId: selectedSection || undefined,
      offset: (page - 1) * size,
      limit: size,
      // sortColumn,
      // sortDirection,
    }));
  };

  useEffect(() => {
    if (selectedSection === undefined) return;

    setCurrentPage(1);

    if (isCustomDate) {
      applyCustomDate(1);
    } else {
      applyPreset(selectedPreset, 1);
    }
  }, [selectedSection]);

  const applyCustomDate = (page = 1, size = pageSize) => {
    setCurrentPage(page);

    setFilters((prev) => ({
      ...prev,
      period: null,
      startDate,
      endDate,
      sectionId: selectedSection || undefined,
      offset: (page - 1) * size,
      limit: size,
      // sortColumn,
      // sortDirection,
    }));
  };

  useEffect(() => {
    applyPreset("today");
  }, []);

  //export file as csv
  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map((c) => `"${c.label}"`).join(",");
    const rows = data.map((r) =>
      columns.map((c) => `"${r[c.key] ?? ""}"`).join(",")
    );

    const blob = new Blob([headers, "\n", rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "table-utilization-report.csv";
    link.click();
  };

  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);

  useEffect(() => {
    const loadSections = async () => {
      try {
        setLoadingSections(true);

        const res = await sectionService.getAll({ isActive: true });

        const sectionsArray = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];

        setSections([{ id: "", name: "All Sections" }, ...sectionsArray]);
      } catch (err) {
      } finally {
        setLoadingSections(false);
      }
    };

    loadSections();
  }, []);

  const summaryCards = [
    {
      title: "Total Tables",
      value: summary?.totalTables ?? 0,
      subtitle: "Active tables",
      icon: LayoutGrid,
    },
    {
      title: "Total Sessions",
      value: summary?.totalSessions ?? 0,
      subtitle: "Table turns",
      icon: ArrowUpRight,
    },
    {
      title: "Total Revenue",
      value: `₹${summary?.totalRevenue?.toLocaleString() ?? 0}`,
      subtitle: "From dine-in",
      icon: DollarSign,
    },
    {
      title: "Avg Sessions/Table",
      value: summary?.avgSessionsPerTable?.toFixed(2) ?? 0,
      subtitle: "Per table",
      icon: TrendingUp,
    },
    {
      title: "Avg Duration",
      value: `${summary?.avgSessionDurationMins?.toFixed(0) ?? 0} min`,
      subtitle: "Per session",
      icon: Clock,
    },
    {
      title: "Avg Revenue/Table",
      value: `₹${summary?.avgRevenuePerTable?.toLocaleString() ?? 0}`,
      subtitle: "Per table",
      icon: DollarSign,
    },
  ];

  const columns = [
    {
      key: "tableNumber",
      label: "Table",
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
            <span className="font-bold text-teal-700">{value}</span>
          </div>
          <div>
            <p className="font-medium text-gray-900">{row.sectionName}</p>
            <p className="text-xs text-gray-500">{row.seatingCapacity} seats</p>
          </div>
        </div>
      ),
    },
    {
      key: "totalSessions",
      label: "Sessions",
      sortable: true,
      align: "right",
    },
    {
      key: "totalRevenue",
      label: "Revenue",
      sortable: true,
      align: "right",
      render: (value) => (
        <span className="font-semibold text-teal-700">
          ₹{value.toLocaleString()}
        </span>
      ),
    },
    {
      key: "avgRevenuePerSession",
      label: "Avg/Session",
      sortable: true,
      align: "right",
    },
    {
      key: "avgSessionDurationMins",
      label: "Avg Duration",
      sortable: true,
      align: "right",
      render: (value) => (
        <div className="flex items-center justify-end gap-1">
          <Timer size={14} className="text-gray-400" />
          <span className="text-gray-600">{value ?? 0} min</span>
        </div>
      ),
    },
    {
      key: "avgGuestsPerSession",
      label: "Avg Guests",
      sortable: true,
      align: "right",
      render: (value, row) => (
        <span className="text-gray-600">
          {(value ?? 0).toFixed(1)}/{row.seatingCapacity}
        </span>
      ),
    },
    {
      key: "revenuePerSeat",
      label: "Rev/Seat",
      sortable: true,
      align: "right",
      render: (value) => (
        <span className="text-gray-600">₹{(value ?? 0).toLocaleString()}</span>
      ),
    },
    {
      key: "utilizationScore",
      label: "Utilization",
      sortable: true,
      width: "140px",
      render: (value) => (
        <div className="w-28">
          <ProgressBar
            value={value ?? 0}
            max={100}
            color={value >= 80 ? "green" : value >= 50 ? "amber" : "red"}
          />
        </div>
      ),
    },
  ];

const handleSort = (column) => {
  let nextDirection;

  if (sortColumn === column) {
    nextDirection = sortDirection === "asc" ? "desc" : "asc";
  } else {
    nextDirection = "desc";
  }

  setSortColumn(column);
  setSortDirection(nextDirection);
};


  const handleRefresh = () => {
    setFilters((prev) => ({
      ...prev,
      period: selectedPreset,
      startDate,
      endDate,
      sectionId: selectedSection || undefined,
      offset: 0,
    }));
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
  };

  const filters = (
    <div className="flex flex-col gap-4">
      <div className="flex items-end gap-3 flex-wrap">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          selectedPreset={selectedPreset}
          onPresetSelect={(preset) => {
            setSelectedPreset(preset);
            setIsCustomDate(false);
            applyPreset(preset);
          }}
          onStartDateChange={(d) => {
            setStartDate(formatDate(d));
            setSelectedPreset(null);
            setIsCustomDate(true);
          }}
          onEndDateChange={(d) => {
            setEndDate(formatDate(d));
            setSelectedPreset(null);
            setIsCustomDate(true);
          }}
        />
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Section</label>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            disabled={loadingSections}
            className="h-9 px-3 text-sm border border-gray-300 rounded-lg min-w-[180px]"
          >
            {sections.map((s) => (
              <option key={s.id || "all"} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <button
          disabled={!isCustomDate}
          onClick={() => applyCustomDate()}
          className={`h-9 px-5 rounded-lg text-sm font-medium ${
            isCustomDate
              ? "bg-teal-600 text-white hover:bg-teal-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Apply Filters
        </button>

        {branchOptions.length > 0 && (
          <div className="relative">
            <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={selectedBranchId ?? ''}
              onChange={(e) => {
                const val = e.target.value || null;
                setSelectedBranchId(val);
                setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
              }}
              className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
            >
              <option value="">All Branches</option>
              {branchOptions.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>
    </div>
  );

  const pagination = {
    currentPage,
    totalPages: Math.ceil(totalItems / pageSize),
    totalItems,
    pageSize,
    onPageChange: (page) =>
      isCustomDate ? applyCustomDate(page) : applyPreset(selectedPreset, page),
    onPageSizeChange: (size) => {
      setPageSize(size);
      isCustomDate
        ? applyCustomDate(1, size)
        : applyPreset(selectedPreset, 1, size);
    },
  };

  const topTables = [...data]
    .filter((t) => t.totalSessions > 0)
    .sort((a, b) => b.utilizationScore - a.utilizationScore)
    .slice(0, 3);

  const underutilizedTables = [...data]
    .filter((t) => t.totalSessions > 0 && t.utilizationScore < 40)
    .sort((a, b) => a.utilizationScore - b.utilizationScore)
    .slice(0, 3);

  const sectionSummary = Object.values(
    data.reduce((acc, t) => {
      acc[t.sectionName] ??= {
        sectionName: t.sectionName,
        revenue: 0,
        tables: 0,
      };
      acc[t.sectionName].revenue += t.totalRevenue || 0;
      acc[t.sectionName].tables += 1;
      return acc;
    }, {})
  );

   //SORTING
const sortedData = useMemo(() => {
  if (!data || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDirection === "asc"
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === "asc"
      ? String(aVal ?? "").localeCompare(String(bVal ?? ""))
      : String(bVal ?? "").localeCompare(String(aVal ?? ""));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in table number
    if (String(row.tableNumber).toLowerCase().includes(query)) return true;
    // Search in section name
    if (String(row.sectionName || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['totalSessions', 'totalRevenue', 'avgRevenuePerSession', 'avgSessionDurationMins', 'avgGuestsPerSession', 'revenuePerSeat', 'utilizationScore', 'seatingCapacity'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Table Utilization Report"
      description="Identify underperforming tables and optimize seating arrangement"
      icon={LayoutGrid}
      exportPermission={P.REPORTS.OPERATIONS.TABLE_EXPORT}
      color="purple"
      category="Operations"
      categoryId="operational"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      error={error}
      pagination={pagination}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      onRefresh={() =>
        isCustomDate ? applyCustomDate() : applyPreset(selectedPreset)
      }
      onExport={(format) => {
        if (format === "csv") exportToCsv();
      }}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by table, section..."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={16} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Top Tables</h3>
              <p className="text-xs text-gray-500">Highest utilization</p>
            </div>
          </div>

          <div className="space-y-2">
            {topTables.map((table) => (
              <div
                key={table.tableId}
                className="flex items-center justify-between p-2 bg-green-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-green-200 text-green-700 rounded-lg flex items-center justify-center text-xs font-bold">
                    {table.tableNumber}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {table.sectionName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {table.totalSessions} sessions
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-green-600">
                  {table.utilizationScore.toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Armchair size={16} className="text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Underutilized</h3>
              <p className="text-xs text-gray-500">Needs attention</p>
            </div>
          </div>

          <div className="space-y-2">
            {underutilizedTables.map((table) => (
              <div
                key={table.tableId}
                className="flex items-center justify-between p-2 bg-red-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-red-200 text-red-700 rounded-lg flex items-center justify-center text-xs font-bold">
                    {table.tableNumber}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {table.sectionName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {table.totalSessions} sessions
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-red-600">
                  {table.utilizationScore.toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <LayoutGrid size={16} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">By Section</h3>
              <p className="text-xs text-gray-500">Revenue breakdown</p>
            </div>
          </div>

          <div className="space-y-3">
            {sectionSummary.map((section) => (
              <div
                key={section.sectionName}
                className="p-2 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {section.sectionName}
                  </span>
                  <span className="text-sm font-semibold text-teal-600">
                    ₹{section.revenue.toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{section.tables} tables</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ReportLayout>
  );
}
